// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { AssetConstructionType, AssetType } from '@shared/constants/asset';
import { CustomerDTO } from './customers/customer';
import { AddressParts } from './sp-sheets';

// Struct to store info about an asset (location, type, ...)
export interface AssetDTO {
  id: number;
  name: string;
  serialNumber: string;
  legacyIDCode: string;
  customerId: CustomerDTO['id'];
  isBlocked?: boolean;
  isActive?: boolean;
  customerName?: CustomerDTO['name'];
  type?: AssetType;
  assetImageUrl?: string;
  imageInfo?: {
    image?: string;
    imageSet?: boolean;
  };
  address?: AddressParts;
  coordinates?: {
    lat: number;
    lon: number;
  };
  note?: string;
  construction?: {
    height: number;
    type: AssetConstructionType;
    // There may more props here. TBD: use a dict instead of fixed fields.
  };
  lastUpdated?: string;
  createdBy?: string;
  createdByEmail?: string;
  createdById?: number;
}

export default class Asset {
  id: number;
  name: string;
  isBlocked: boolean;
  isActive: boolean;
  serialNumber: string;
  legacyIDCode: string;
  customerId: CustomerDTO['id'];
  customerName: CustomerDTO['name'];
  type: AssetType;
  assetImageUrl: string;
  imageInfo: {
    image?: string;
    imageSet?: boolean;
  };
  address: AddressParts;
  coordinates: {
    lat: number;
    lon: number;
  };
  note: string;
  construction: {
    height: number;
    buildingHeight: number;
    type: AssetConstructionType;
    siteType: string;
    numberOfTenants: number;
    complexityRating: number;
    manufacture: string;
    model: string;
    technology: string;
    usefulLoadCapacity: number;
    reinforceNReplacement: string;
  };
  lastUpdated: string;
  createdBy: string;
  createdByEmail: string;
  createdById: number;

  constructor(dto: Partial<AssetDTO>) {
    this.update(dto);
  }

  get asJson(): AssetDTO {
    return {
      id: this.id,
      name: this.name,
      serialNumber: this.serialNumber,
      legacyIDCode: this.legacyIDCode,
      isBlocked: this.isBlocked,
      isActive: this.isActive,
      customerId: this.customerId,
      customerName: this.customerName,
      type: this.type,
      assetImageUrl: this.assetImageUrl,
      imageInfo: this.imageInfo,
      address: this.address,
      coordinates: this.coordinates,
      note: this.note,
      construction: this.construction,
      lastUpdated: this.lastUpdated,
      createdBy: this.createdBy,
      createdById: this.createdById,
      createdByEmail: this.createdByEmail
    };
  }

  update(newData: Partial<AssetDTO>): AssetDTO {
    const allData = { ...this.asJson, ...newData };

    Object.assign(this, allData);

    return this.asJson;
  }
}